// src/components/IconReplacement/IconReplacementManager.tsx
// ✨ Manages the icon replacement confirmation dialog
// Supports both: PlaceIcon mode (sidebar) and DragItems mode (existing icons)
import React, { useEffect, useState, useCallback } from 'react';
import { ConfirmIconReplaceDialog } from '../Dialogs/ConfirmIconReplaceDialog';
import {
  registerConfirmDialog,
  confirmIconReplacement,
  cancelIconReplacement
} from '@/interaction/modes/PlaceIcon';
import {
  registerDragReplaceDialog,
  confirmDragReplacement,
  cancelDragReplacement
} from '@/interaction/modes/DragItems';
import { useScene } from '@/hooks/useScene';
import { useUiStateStore } from '@/stores/uiStateStore';
import { useModelItem } from '@/hooks/useModelItem';
import { useIcon } from '@/hooks/useIcon';

// Params for PlaceIcon mode (from sidebar)
interface PlaceIconReplacementParams {
  type: 'place';
  existingItemId: string;
  newIconId: string;
  tile: { x: number; y: number };
}

// Params for DragItems mode (dragging existing icon)
interface DragReplacementParams {
  type: 'drag';
  sourceItemId: string;
  targetItemId: string;
  sourceIconId: string;
}

type ReplacementParams = PlaceIconReplacementParams | DragReplacementParams;

export const IconReplacementManager: React.FC = () => {
  const [replacementParams, setReplacementParams] =
    useState<ReplacementParams | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const scene = useScene();
  const uiStateActions = useUiStateStore((state) => state.actions);

  // Register PlaceIcon dialog callback on mount
  useEffect(() => {
    const handlePlaceIconReplacement = (params: {
      existingItemId: string;
      newIconId: string;
      tile: { x: number; y: number };
    }) => {
      setReplacementParams({
        type: 'place',
        ...params
      });
      setIsDialogOpen(true);
    };

    registerConfirmDialog(handlePlaceIconReplacement);

    return () => {
      registerConfirmDialog(() => {});
    };
  }, []);

  // Register DragItems dialog callback on mount
  useEffect(() => {
    const handleDragReplacement = (params: {
      sourceItemId: string;
      targetItemId: string;
      sourceIconId: string;
    }) => {
      setReplacementParams({
        type: 'drag',
        ...params
      });
      setIsDialogOpen(true);
    };

    registerDragReplaceDialog(handleDragReplacement);

    return () => {
      registerDragReplaceDialog(() => {});
    };
  }, []);

  const handleConfirm = useCallback(() => {
    if (!replacementParams) return;

    if (replacementParams.type === 'place') {
      confirmIconReplacement(scene, uiStateActions);
    } else {
      confirmDragReplacement(scene, uiStateActions);
    }

    setIsDialogOpen(false);
    setReplacementParams(null);
  }, [replacementParams, scene, uiStateActions]);

  const handleCancel = useCallback(() => {
    if (!replacementParams) return;

    if (replacementParams.type === 'place') {
      cancelIconReplacement(uiStateActions);
    } else {
      cancelDragReplacement(uiStateActions);
    }

    setIsDialogOpen(false);
    setReplacementParams(null);
  }, [replacementParams, uiStateActions]);

  // ✅ FIX: Return early BEFORE calling hooks with invalid IDs
  if (!replacementParams) {
    return null;
  }

  // Determine which IDs to use based on replacement type
  const targetItemId = replacementParams.type === 'place'
    ? replacementParams.existingItemId
    : replacementParams.targetItemId;

  const newIconId = replacementParams.type === 'place'
    ? replacementParams.newIconId
    : replacementParams.sourceIconId;

  // For drag mode, we also need the source item info
  const sourceItemId = replacementParams.type === 'drag'
    ? replacementParams.sourceItemId
    : undefined;

  return (
    <IconReplacementDialog
      isOpen={isDialogOpen}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      targetItemId={targetItemId}
      newIconId={newIconId}
      sourceItemId={sourceItemId}
      isDragMode={replacementParams.type === 'drag'}
    />
  );
};

/**
 * Separate component that handles the dialog rendering with hooks
 * This component only renders when we have valid IDs
 */
interface IconReplacementDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  targetItemId: string;
  newIconId: string;
  sourceItemId?: string;
  isDragMode: boolean;
}

const IconReplacementDialog: React.FC<IconReplacementDialogProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  targetItemId,
  newIconId,
  sourceItemId,
  isDragMode
}) => {
  // Get target item info (the one being replaced)
  const targetModelItem = useModelItem(targetItemId);
  const targetIcon = useIcon(targetModelItem?.icon || '', targetItemId);

  // Get new icon details
  const newIcon = useIcon(newIconId, `preview-${newIconId}`);

  // For drag mode, get source item name
  const sourceModelItem = useModelItem(sourceItemId || '');

  // Determine dialog text based on mode
  const oldIconName = targetIcon.icon?.name || targetModelItem?.name || 'Unknown';
  const newIconName = isDragMode
    ? (newIcon.icon?.name || sourceModelItem?.name || 'Unknown')
    : (newIcon.icon?.name || 'Unknown');

  return (
    <ConfirmIconReplaceDialog
      isOpen={isOpen}
      onConfirm={onConfirm}
      onCancel={onCancel}
      oldIconName={oldIconName}
      newIconName={newIconName}
      oldIconUrl={targetIcon.icon?.url}
      newIconUrl={newIcon.icon?.url}
    />
  );
};
